import React, { useState } from 'react';
import { IconButton, Button, Icon, TextField, Tooltip } from '@material-ui/core';
import useStyles from './TaskDetail.styles';
import { useDispatch, useSelector } from 'react-redux';
import { get, size, first, set, flatten } from 'global/lib/lodash';
import { formatDateFromNow } from 'global/utils/formatDate';
import { getTaskStatus, stopProp } from 'global/utils';
import Image from 'global/components/image';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import Comment from 'global/components/comment';
import sendData from 'redux/actions/sendData';
import DropArea from 'global/components/drop-area';
import Confirm from 'global/components/confirm';
import { setView } from 'redux/actions/view';
import { useUser } from 'global/hooks/useUser';
import { Text } from 'global/components/text';

export const CommentContainer = (props) => {
  const { comments, formState, data, onSubmitComment } = props;

  const classes = useStyles();

  let [createComment, setCreateComment] = useState();
  const [photoDrop, setPhotoDrop] = useState(false);
  const [confirmProps, setConfirmProps] = useState({ maxWidth: 'sm' });

  const dispatch = useDispatch();
  const { me } = useUser();
  const dontAskTaskDelete = useSelector((state) => get(state, 'view.dontAskTaskDelete'));
  const authors = get(data, 'authors', []);
  const assignees = get(data, 'assignees', []);
  const isAuthor = authors.find((a) => a.id == me.id);
  const isAssignee = assignees.find((a) => a.id == me.id);
  const status = getTaskStatus(data);

  const submitComment = async () => {
    const comms = [...comments, createComment];
    await onSubmitComment(comms);
    setCreateComment();
    setPhotoDrop(false);
  };

  const clickComment = (opts = {}) => {
    createComment = {
      isNew: true,
      isCorrection: false,
      newStatus: 'closed',
      body: '',
      ...opts,
    };
    setCreateComment({ ...createComment });
  };

  const onFilesChange = (files) => {
    set(createComment, 'images', [...files]);
    setCreateComment({ ...createComment });
  };

  const deleteImage = (file, index) => {
    const photos = get(createComment, 'images', []);
    photos.splice(index, 1);
    set(createComment, 'images', photos);
    setCreateComment({ ...createComment });
  };

  const closeoutClick = () => {
    const closeConfirm = () => {
      setConfirmProps((cp) => ({ ...cp, title: '' }));
    };

    const deleteImages = async () => {
      try {
        await Promise.all(
          allImages().map(async (ci) => {
            await sendData({
              url: `/media/${ci.id}`,
              method: 'delete',
            });
          })
        );
        set(formState, 'images', []);
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    };

    const closetask = () => {
      clickComment({ newStatus: 'closed', body: 'Task closed' });
      submitComment();
    };

    const completeCloseout = async () => {
      await deleteImages();
      closetask();
      closeConfirm();
    };

    if (status.status !== 1) {
      //author direct closeout... special conditions
      if (dontAskTaskDelete) {
        completeCloseout();
      } else {
        setConfirmProps({
          ...confirmProps,
          title: 'Are you sure?',
          content: 'You are closing a task without uploading correction photos first, this will also delete all the task photos. Would you like to continue?',
          okayText: 'Closeout and Delete Photos',
          showDontAsk: true,
          onCancel: closeConfirm,
          onOk: async (dontAsk) => {
            dispatch(setView({ dontAskTaskDelete: dontAsk }));
            await completeCloseout();
          },
        });
      }
    } else {
      closetask();
    }
  };

  const commentDates = () => {
    const plural = size(comments) > 1;
    let str = `${size(comments)} comment${plural || !size(comments) ? 's' : ''}`;
    if (size(comments)) {
      str += ` - last updated ${formatDateFromNow(get(first(comments), 'createdAt', new Date()))}`;
    }
    return str;
  };

  const commentDoneDisabled = () => {
    const isCorrection = get(createComment, 'isCorrection');
    const bodysize = size(get(createComment, 'body'));
    let dis = !bodysize;
    let text = 'Description required to submit.';
    if (isCorrection) {
      dis = !size(get(createComment, 'images')) || !bodysize;
      text = 'Description and photo required to add correction.';
    }
    return {
      disabled: dis,
      text,
    };
  };

  const allImages = () => {
    const commentHas = flatten(comments.filter((c) => !!size(c.images)).map((c) => c.images));
    const taskHas = get(data, '0.images', []);

    return [...(commentHas || []), ...(taskHas || [])];
  };

  const hasImages = !!size(allImages());

  const closeoutTitle = `Only the Author of a task can use the "Closeout" button to submit a comment that changes the status to "Closed".${
    hasImages ? ' Closing a task without uploading a correction will also remove the original task photos.' : ''
  }`;

  return (
    <div className={classes.commentContainer}>
      <div className={classes.commentsWrap}>
        <Text type="title_large">Comments</Text>
        <div className={classes.commentCount}>{commentDates()}</div>
        {comments.map((c, i) => (
          <Comment key={i} {...c} />
        ))}
      </div>

      {createComment && (
        <div className={classes.createCommentContainer}>
          <div className={classes.leftIcon}>
            <Icon>{getTaskStatus(createComment.newStatus).icon}</Icon>
          </div>

          <div className={classes.textContainer}>
            <div>
              <div className={classes.titleContainer}>{createComment && createComment.isCorrection ? 'Add Correction' : 'Add Comment'}</div>

              {createComment.isCorrection && (
                <div className={classes.correctionToggleContainer}>
                  <ToggleButtonGroup fullWidth={true} size={'small'} onChange={(e, newStatus) => setCreateComment({ ...createComment, newStatus })} exclusive={true} value={createComment.newStatus}>
                    <ToggleButton value="pending">
                      <Icon className={classes.correctionBtnIcon}>check_circle_outline</Icon> Pending
                    </ToggleButton>
                    <ToggleButton value="closed" disabled={!isAuthor}>
                      <Icon className={classes.correctionBtnIcon}>check_circle_fill</Icon> Closed
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              )}

              <TextField
                multiline={true}
                value={createComment.body}
                rows={5}
                variant="outlined"
                fullWidth={true}
                className={classes.commentText}
                onChange={(e) => setCreateComment({ ...createComment, body: e.target.value })}
              />
            </div>

            <div>
              {photoDrop && (
                <DropArea
                  accept={'image/*'}
                  onFilesChange={onFilesChange}
                  files={get(createComment, 'images', [])}
                  renderContent={() => <div>Drop Files here. Or click here.</div>}
                  renderFile={(file, i) => {
                    const loc = `images.${i}.caption`;
                    return (
                      <div onClick={stopProp}>
                        <Image style={{ overflow: 'hidden' }} useNaturalDims={false} imageStyle={{ width: 'auto', margin: 'auto', right: 0, bottom: 0 }} src={file.data || file.src} />
                        <TextField
                          placeholder="caption"
                          value={get(createComment, loc)}
                          onChange={(e) => {
                            set(createComment, loc, e.target.value);
                            setCreateComment({ ...createComment });
                          }}
                          className={classes.comment}
                        />
                        <IconButton className={classes.imageDelete} onClick={() => deleteImage(file, i)}>
                          <Icon>close</Icon>
                        </IconButton>
                      </div>
                    );
                  }}
                />
              )}
              <Button className={classes.addCommentPhoto} variant={'contained'} color="secondary" onClick={() => setPhotoDrop(!photoDrop)}>
                <Icon>add_a_photo</Icon>
              </Button>

              <div className={classes.submitButtons}>
                <Button variant={'contained'} color="primary" startIcon={<Icon>close</Icon>} onClick={() => setCreateComment()}>
                  Cancel
                </Button>

                <Tooltip title={commentDoneDisabled().text}>
                  <span>
                    <Button variant={'contained'} color="secondary" startIcon={<Icon>check</Icon>} disabled={commentDoneDisabled().disabled} onClick={() => submitComment()}>
                      Done
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      )}

      {!createComment && (
        <div className={classes.commentBtnContainer}>
          <Button className={classes.commentBtn} variant={'contained'} color="secondary" startIcon={<Icon>add_comment</Icon>} onClick={() => clickComment({ newStatus: status.title })}>
            Comment
          </Button>

          {status.status === 0 && (
            <Tooltip title={`Only the author or assignee of a task can change its status.`}>
              <span>
                <Button
                  className={classes.commentBtn}
                  variant={'contained'}
                  color="secondary"
                  disabled={isAuthor || isAssignee ? false : true}
                  startIcon={<Icon>check_circle_outline</Icon>}
                  onClick={() => clickComment({ isCorrection: true })}
                >
                  Add Correction
                </Button>
              </span>
            </Tooltip>
          )}

          {(status.status === 1 || status.status === 2) && (
            <Tooltip title={`Only the author or assignee of a task can change its status.`}>
              <span>
                <Button
                  className={classes.commentBtn}
                  variant={'contained'}
                  color="secondary"
                  disabled={isAuthor || isAssignee ? false : true}
                  startIcon={<Icon>radio_button_unchecked</Icon>}
                  onClick={() => clickComment({ newStatus: 'open' })}
                >
                  Re-open
                </Button>
              </span>
            </Tooltip>
          )}

          {(status.status === 1 || (isAuthor && status.status !== 2)) && (
            <Tooltip title={closeoutTitle}>
              <span>
                <Button className={classes.commentBtn} variant={'contained'} color="secondary" startIcon={<Icon>check_circle</Icon>} disabled={!isAuthor} onClick={closeoutClick}>
                  Closeout
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      )}

      <Confirm {...confirmProps} />
    </div>
  );
};
